import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi } from '../../services/settings';

const defaultForm: Record<string, string | boolean> = {
  contactName: '',
  contactAddress: '',
  orgNr: '',
  bankgiro: '',
  email: '',
  web: '',
  phone: '',
  footerText: '',
  showApisAiBranding: true,
};

export function BusinessPrintSettingsPage() {
  const [form, setForm] = useState<Record<string, string | boolean>>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { error, success, info } = useToast();

  const load = useCallback(async () => {
    try {
      const data = await settingsApi.getBusinessPrintSettings();
      setForm({
        contactName: data.contactName ?? '',
        contactAddress: data.contactAddress ?? '',
        orgNr: data.orgNr ?? '',
        bankgiro: data.bankgiro ?? '',
        email: data.email ?? '',
        web: data.web ?? '',
        phone: data.phone ?? '',
        footerText: data.footerText ?? '',
        showApisAiBranding: true,
      });
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte hämta inställningar');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateBusinessPrintSettings({
        contactName: (form.contactName as string) || null,
        contactAddress: (form.contactAddress as string) || null,
        orgNr: (form.orgNr as string) || null,
        bankgiro: (form.bankgiro as string) || null,
        email: (form.email as string) || null,
        web: (form.web as string) || null,
        phone: (form.phone as string) || null,
        footerText: (form.footerText as string) || null,
        showApisAiBranding: true,
      });
      success('Utskriftsinställningar sparade');
    } catch (e) {
      error(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Utskriftsinställningar</h2>
      <p className="text-sm text-gray-600">Kontakt- och sidfotsinformation som används vid utskrift av PDF:er.</p>
      <div className="border border-gray-200 rounded p-3 bg-gray-50 space-y-2 max-w-lg">
        <label className="block text-sm font-medium">Kontaktnamn</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.contactName as string}
          onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
        />
        <label className="block text-sm font-medium">Adress</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.contactAddress as string}
          onChange={(e) => setForm((f) => ({ ...f, contactAddress: e.target.value }))}
        />
        <label className="block text-sm font-medium">Org.nr</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.orgNr as string}
          onChange={(e) => setForm((f) => ({ ...f, orgNr: e.target.value }))}
        />
        <label className="block text-sm font-medium">Bankgiro</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.bankgiro as string}
          onChange={(e) => setForm((f) => ({ ...f, bankgiro: e.target.value }))}
        />
        <label className="block text-sm font-medium">E-post</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.email as string}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <label className="block text-sm font-medium">Webb</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.web as string}
          onChange={(e) => setForm((f) => ({ ...f, web: e.target.value }))}
        />
        <label className="block text-sm font-medium">Telefon</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.phone as string}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
        />
        <label className="block text-sm font-medium">Sidfotstext</label>
        <input
          type="text"
          className="w-full border rounded px-2 py-1 text-sm"
          value={form.footerText as string}
          onChange={(e) => setForm((f) => ({ ...f, footerText: e.target.value }))}
        />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={true}
            disabled
            onChange={() => info('Denna inställning kräver högsta betalningsplan.')}
          />
          <span className="text-sm">Visa ApisAI-märkning (kräver högsta plan för att kunna avmarkeras)</span>
        </label>
        <WithWrite moduleCode="settings">
          <button type="button" className="px-2 py-1 text-sm bg-primary text-white rounded" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar...' : 'Spara'}
          </button>
        </WithWrite>
      </div>
    </div>
  );
}

